var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-507 / CZNA-531 — the "Výchozí hodnota" control for one subfield.
 *
 * Until CZNA-507 it was a plain text box for every subfield, including the
 * ones bound to a controlled vocabulary. An admin could therefore type a value
 * the cataloguer's picker will refuse and the backend `VocabularyValidator`
 * will reject — the form pre-fills a value that cannot be saved.
 *
 * CZNA-531: "backed by a vocabulary" turned out to be only one of three ways a
 * subfield offers the cataloguer a list. This component now resolves the source
 * first (see `utils/defaultValueSource.ts`) and then picks a control:
 *
 *   - static `selectOptions` → a `<Select>` over those literals, here;
 *   - authority record picker → `AuthorityDefaultValueEditor` (heading + FK);
 *   - vocabulary → the CZNA-507 table below;
 *   - nothing → free text, unchanged.
 *
 * Resolution order matters and mirrors the RENDERER's: `RecordPickerSubfield`
 * short-circuits on `selectOptions` before reading `recordType`, so the editor
 * checks it first too. Offering a list the form will not show would be worse
 * than the text box this ticket is replacing.
 *
 * Which vocabulary control is rendered depends on what we can actually promise:
 *
 * | situation                              | control        | why |
 * |----------------------------------------|----------------|-----|
 * | no vocabulary binding                   | text           | unchanged behaviour |
 * | CLOSED + full term list on screen       | select         | free text is never valid here, so do not offer it |
 * | OPEN + terms available                  | text + datalist| the list is a suggestion; the cataloguer may add terms |
 * | vocabulary has NO terms                 | text + warning | a select would leave the field unsettable |
 * | term list truncated / failed to load    | text + warning | a partial list looks exactly like a complete one |
 *
 * The last two rows are the point. 21 of the 88 vocabulary-bound subfields in
 * the shipped templates point at a CLOSED vocabulary with **zero** terms today
 * (VOCABULARY_LANGUAGE alone covers 7 of them). Rendering an empty select there
 * would have taken a working field away and looked like a new defect.
 *
 * A value already stored outside the vocabulary is never silently dropped: it
 * is kept as an extra, flagged option so the admin can see what is wrong and
 * decide. Blanking it on render is the failure mode that made CZNA-483
 * expensive.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField, Select } from '@folio/stripes/components';
import useVocabularyTerms from '../../hooks/useVocabularyTerms';
import { useVocabularyTypes } from '../../hooks/useVocabularyTypes';
import { resolveVocabularyBinding } from './utils/vocabularyBinding';
import { authorityPickerTarget, hasStaticOptions } from './utils/defaultValueSource';
import AuthorityDefaultValueEditor from './AuthorityDefaultValueEditor';
import { resolveLabel } from '../../components/ProposalForm/renderer/labelResolver';
import { vocabularyOptionLabel, vocabularyStoredValue, } from '../../components/ProposalForm/utils/vocabularyCodeValue';
var HINT_STYLE = {
    fontSize: '0.75rem',
    color: '#a06000',
    marginTop: '0.2rem',
};
/**
 * `selectOptions` — a handful of literals baked into the config. The list is
 * the entire domain (nothing can be added at runtime, unlike a vocabulary), so
 * this is the one branch that can restrict unconditionally.
 */
var StaticOptionsDefaultValueEditor = function (_a) {
    var _b, _c;
    var subfield = _a.subfield, onChange = _a.onChange, testId = _a["data-testid"];
    var intl = useIntl();
    var label = intl.formatMessage({
        id: 'ui-czna-authorities.settings.forms.visual.property.defaultValue',
    });
    var current = (_b = subfield.defaultValue) !== null && _b !== void 0 ? _b : '';
    var options = ((_c = subfield.selectOptions) !== null && _c !== void 0 ? _c : []).map(function (opt) { return ({
        value: opt.value,
        label: resolveLabel(opt, intl, opt.value),
    }); });
    // Same rule as the CLOSED-vocabulary branch: a stored value outside the list
    // is shown and flagged, never silently snapped to '—'.
    var isOutside = current !== '' && !options.some(function (o) { return o.value === current; });
    return (React.createElement("div", null,
        React.createElement(Select, { value: current, onChange: function (e) {
                onChange(e.target.value === '' ? undefined : e.target.value);
            }, label: label, dataOptions: __spreadArray(__spreadArray([
                { value: '', label: '—' }
            ], (isOutside
                ? [{
                        value: current,
                        label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.defaultValue.outsideOption' }, { value: current }),
                    }]
                : []), true), options, true), "data-testid": testId }),
        isOutside && (React.createElement("div", { style: __assign(__assign({}, HINT_STYLE), { color: '#c00' }), "data-testid": "".concat(testId, "-options-outside") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.optionsOutside" })))));
};
var VocabularyDefaultValueEditor = function (_a) {
    var _b;
    var subfield = _a.subfield, fieldTag = _a.fieldTag, onChange = _a.onChange, testId = _a["data-testid"];
    var intl = useIntl();
    var label = intl.formatMessage({
        id: 'ui-czna-authorities.settings.forms.visual.property.defaultValue',
    });
    // `includeBindings` is what makes the registry half of the resolution work;
    // without it every subfield looks unbound. Shared cache key with the other
    // callers of this hook, so it is one request per editor session.
    var vocabularyTypes = useVocabularyTypes({ includeBindings: true }).vocabularyTypes;
    var binding = resolveVocabularyBinding(subfield, fieldTag, vocabularyTypes);
    var _c = useVocabularyTerms(binding === null || binding === void 0 ? void 0 : binding.vocabularyType), terms = _c.terms, isTruncated = _c.isTruncated, isLoading = _c.isLoading, isError = _c.isError;
    var current = (_b = subfield.defaultValue) !== null && _b !== void 0 ? _b : '';
    var handleText = function (e) {
        onChange(e.target.value === '' ? undefined : e.target.value);
    };
    // No vocabulary behind this subfield — the original control, unchanged.
    if (!binding) {
        return (React.createElement(TextField, { value: current, onChange: handleText, label: label, "data-testid": testId }));
    }
    // CZNA-940: what the admin picks here becomes a subfield's stored default, so it obeys
    // the same rule as a cataloguer's pick — the CODE for a code-bearing register (language),
    // the term's name for every other. The label keeps showing the name, so the two differ
    // exactly where the register carries both. Today the seeded languages have no 901$c and
    // their name IS the code, so both halves return the same string and nothing moves; the
    // moment CZNA-942 puts Czech names on them, a default of `čeština` would put an illegal
    // value into 377$a of every new record — and a form-config default is frozen behind
    // `customized=true`, which makes that the expensive direction to get wrong.
    var termOptions = terms.map(function (t) { return ({
        value: vocabularyStoredValue(binding.vocabularyType, t.name, t.code, vocabularyTypes),
        label: vocabularyOptionLabel(t.name, t.code),
    }); });
    var termValues = termOptions.map(function (o) { return o.value; });
    var datalistId = "".concat(testId, "-options");
    /**
     * Free text with the vocabulary as suggestions. Used for OPEN vocabularies
     * and as the honest fallback whenever we cannot show the complete list.
     */
    var textWithSuggestions = function (hint) { return (React.createElement("div", null,
        React.createElement(TextField, { value: current, onChange: handleText, label: label, "data-testid": testId, list: termOptions.length > 0 ? datalistId : undefined }),
        termOptions.length > 0 && (React.createElement("datalist", { id: datalistId, "data-testid": "".concat(testId, "-datalist") }, termOptions.map(function (opt) { return (React.createElement("option", { key: opt.value, value: opt.value }, opt.label)); }))),
        hint)); };
    if (isLoading) {
        return textWithSuggestions(React.createElement("div", { style: __assign(__assign({}, HINT_STYLE), { color: '#666' }), "data-testid": "".concat(testId, "-loading") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.vocabularyLoading", values: { vocabulary: binding.vocabularyType } })));
    }
    if (isError) {
        return textWithSuggestions(React.createElement("div", { style: HINT_STYLE, "data-testid": "".concat(testId, "-vocabulary-error") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.vocabularyError", values: { vocabulary: binding.vocabularyType } })));
    }
    if (termOptions.length === 0) {
        return textWithSuggestions(React.createElement("div", { style: HINT_STYLE, "data-testid": "".concat(testId, "-vocabulary-empty") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.vocabularyEmpty", values: { vocabulary: binding.vocabularyType } })));
    }
    // A capped list is indistinguishable from a complete one once it is in a
    // select, so never restrict on top of one.
    if (isTruncated || binding.validationType !== 'CLOSED') {
        return textWithSuggestions(isTruncated ? (React.createElement("div", { style: HINT_STYLE, "data-testid": "".concat(testId, "-vocabulary-truncated") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.vocabularyTruncated", values: { vocabulary: binding.vocabularyType, shown: termOptions.length } }))) : (React.createElement("div", { style: __assign(__assign({}, HINT_STYLE), { color: '#666' }), "data-testid": "".concat(testId, "-vocabulary-open") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.vocabularyOpen", values: { vocabulary: binding.vocabularyType } }))));
    }
    // CLOSED, and the list on screen is the whole vocabulary: restrict to it.
    var isOutside = current !== '' && !termValues.includes(current);
    return (React.createElement("div", null,
        React.createElement(Select, { value: current, onChange: function (e) {
                onChange(e.target.value === '' ? undefined : e.target.value);
            }, label: label, dataOptions: __spreadArray(__spreadArray([
                { value: '', label: '—' }
            ], (isOutside
                ? [{
                        value: current,
                        label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.defaultValue.outsideOption' }, { value: current }),
                    }]
                : []), true), termOptions, true), "data-testid": testId }),
        isOutside && (React.createElement("div", { style: __assign(__assign({}, HINT_STYLE), { color: '#c00' }), "data-testid": "".concat(testId, "-vocabulary-outside") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.vocabularyOutside", values: { vocabulary: binding.vocabularyType } })))));
};
/**
 * Dispatcher. Each source gets its own component so that switching a
 * subfield's input kind while the row is open swaps the whole control — the
 * hooks a branch needs (vocabulary terms, record search) then live inside it
 * and are never called conditionally.
 */
var DefaultValueEditor = function (_a) {
    var subfield = _a.subfield, fieldTag = _a.fieldTag, onChange = _a.onChange, testId = _a["data-testid"];
    if (hasStaticOptions(subfield)) {
        return React.createElement(StaticOptionsDefaultValueEditor, { subfield: subfield, onChange: onChange, "data-testid": testId });
    }
    var authorityTarget = authorityPickerTarget(subfield);
    if (authorityTarget) {
        return (React.createElement(AuthorityDefaultValueEditor, { subfield: subfield, recordType: authorityTarget, onChange: onChange, "data-testid": testId }));
    }
    return (React.createElement(VocabularyDefaultValueEditor, { subfield: subfield, fieldTag: fieldTag, onChange: onChange, "data-testid": testId }));
};
export default DefaultValueEditor;
